import React, { FC, useState, useEffect, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { IMultiselect, OptionType } from 'core/interface/autocomplete';
import Image from 'components/image/Image';
import { useField } from 'formik';

import './Index.scss';
import useLocales from 'hooks/useLocales';

const Multiselect: FC<IMultiselect> = ({
  options,
  placeholder,
  className,
  onChange,
  onInputChange,
  value,
  fieldName,
  freeSolo,
  loadMore,
  show_location = false,
  show_arrow = true
}): JSX.Element => {
  const { t } = useLocales();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState('');
  const multiselectRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [field] = useField(fieldName);

  const toggleArrow = () => {
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as Element;

      if (
        multiselectRef.current &&
        !multiselectRef.current.contains(targetElement) &&
        !targetElement.classList.contains('MuiAutocomplete-option')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [multiselectRef]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      const newValue = input.value.trim();
      if (newValue) {
        onChange?.([...(value || []), newValue]);
        setInputValue('');
        onInputChange?.('');
      }
      event.preventDefault();
    }
  };

  const handleScroll = (event: any) => {
    const bottom =
      event.target.scrollHeight - event.target.scrollTop ===
      event.target.clientHeight;
    if (bottom) {
      setScrollPosition(event.target.scrollTop);
      loadMore && loadMore();
    }
  };

  useEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollPosition;
    }
  }, [options]);

  return (
    <div className="multi" ref={multiselectRef}>
      <Autocomplete
        className={`multisel customAutocomplete ${className}`}
        multiple
        freeSolo={freeSolo}
        open={isOpen}
        onOpen={toggleArrow}
        onClose={toggleArrow}
        options={options || []}
        clearIcon={false}
        forcePopupIcon={false}
        value={value}
        inputValue={inputValue}
        filterOptions={(opts) => opts}
        ListboxProps={{
          className: 'scrollable',
          onScroll: handleScroll,
          ref: listboxRef
        }}
        getOptionLabel={(option) => (option as OptionType).title ?? ''}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === 'input') {
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
          } else if (reason === 'clear') {
            setInputValue('');
            onInputChange?.('');
          }
        }}
        onChange={(_, newValue) => {
          setInputValue('');
          onChange?.(newValue as string | OptionType[]);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name={field.name}
            onBlur={field.onBlur}
            placeholder={t(placeholder) || placeholder}
            onKeyDown={handleKeyPress}
          />
        )}
      />
      {show_arrow && (options?.length ?? 0) > 0 && (
        <span onClick={() => setIsOpen(true)}>
          <Image
            className={`arrow-down ${isOpen ? 'active' : ''}`}
            name="down-arrow.svg"
          />
        </span>
      )}
    </div>
  );
};

export default Multiselect;
